import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import HolographicBg from "@/components/HolographicBg";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";

export default function AccessGranted() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const type = params.get("type") || "ticket";
  const isFree = params.get("free") === "1";
  const [status, setStatus] = useState(isFree ? "paid" : "pending");
  const attemptsRef = useRef(0);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isFree || !sessionId) return;
    const poll = async () => {
      if (attemptsRef.current >= 12) { setStatus("timeout"); return; }
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          refresh();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2500);
      }
    };
    poll();
  }, [sessionId, isFree, refresh]);

  return (
    <div className="relative min-h-screen">
      <HolographicBg />
      <TopBar />
      <main className="relative z-20 flex items-center justify-center px-6 py-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
          className="glass-strong rounded-sm p-12 max-w-xl text-center"
          data-testid="access-granted-card"
        >
          {status === "paid" ? (
            <>
              <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-cyan-400">— transmission complete —</div>
              <h1 className="font-display text-5xl font-black uppercase mt-3 neon-cyan">Access Granted</h1>
              {type === "membership" ? (
                <>
                  <p className="font-mono text-sm text-white/80 mt-6 leading-relaxed">
                    You are now in the Inner Circle. 15% off every ticket. Priority everything.
                  </p>
                  <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to="/dashboard" data-testid="ag-dashboard-link" className="gate px-6 py-3 bg-white text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm">Open Dashboard</Link>
                    <Link to="/" data-testid="ag-home-link" className="px-6 py-3 border border-white/20 text-white font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm hover:bg-white/10">Return</Link>
                  </div>
                </>
              ) : type === "merch" ? (
                <>
                  <p className="font-mono text-sm text-white/80 mt-6 leading-relaxed">
                    The vault has received your order. Each relic is hand-made by a divine designer once claimed — you&#39;ll be emailed the moment yours ships.
                  </p>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-fuchsia-400/80 mt-4">
                    Lead time up to one month · Watch your inbox for the shipping notice
                  </p>
                  <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to="/vault" data-testid="ag-vault-link" className="gate px-6 py-3 bg-fuchsia-500 text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm">Back To Vault</Link>
                    <Link to="/influencer/dashboard" data-testid="ag-influencer-dashboard-link" className="px-6 py-3 border border-white/20 text-white font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm hover:bg-white/10">VIP Dashboard</Link>
                  </div>
                </>
              ) : (
                <>
                  <p className="font-mono text-sm text-white/80 mt-6 leading-relaxed">
                    You&#39;re on the guest list. Your QR + line-up code have been sent to your email.
                  </p>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-cyan-400/80 mt-4">
                    Bring two pieces of valid government ID matching your application.
                  </p>
                  <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-white/40">
                    Check spam if you don&#39;t see it within a minute.
                  </div>
                  <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to="/" data-testid="ag-home-link" className="gate px-6 py-3 bg-white text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm">Return To Portal</Link>
                  </div>
                </>
              )}
            </>
          ) : status === "pending" ? (
            <>
              <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-white/60">— verifying transmission —</div>
              <h1 className="font-display text-3xl font-black uppercase mt-3">Processing</h1>
              <p className="font-mono text-sm text-white/60 mt-6">The eye is watching. Do not close this window.</p>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl font-black uppercase text-rose-400">Transmission Lost</h1>
              <p className="font-mono text-sm text-white/60 mt-6">Payment was not completed. Try again from the portal.</p>
              <button onClick={() => navigate("/")} className="mt-8 px-6 py-3 bg-white text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm">Return</button>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}
